import { find, findAll } from "./dom";

// Maste vara samma brytpunkt som i chrome.css. Haller de inte ihop blir
// menyn osynlig i gapet: CSS visar panelen som overlagg medan JS tror att
// den vagrata menyn galler, eller tvartom.
const MOBILE = "(max-width: 1280px)";

/**
 * Mobilmenyn.
 *
 * Panelen är `hidden` som utgångsläge och visas bara under brytpunkten.
 * Över den visar CSS nav-elementet oavsett attributet, så attributet tas
 * bort helt när vi passerar uppåt — annars blir menyn osynlig på desktop
 * för den som råkat stänga den på mobil.
 */
export function initNavigation(): void {
  const toggle = find<HTMLButtonElement>("[data-nav-toggle]");
  const nav = find<HTMLElement>("#huvudmeny");
  if (toggle === null || nav === null) {
    return;
  }

  const label = find<HTMLElement>("[data-nav-label]");
  const masthead = find<HTMLElement>(".masthead");
  const mobile = window.matchMedia(MOBILE);
  const previousInert = new Map<HTMLElement, boolean>();

  /** Keep the covered page out of the accessibility tree until the menu closes. */
  const setBackgroundInert = (open: boolean): void => {
    if (open) {
      for (const element of Array.from(document.body.children)) {
        if (element instanceof HTMLElement && element !== masthead && !previousInert.has(element)) {
          previousInert.set(element, element.inert);
          element.inert = true;
        }
      }
    } else {
      for (const [element, inert] of previousInert) {
        element.inert = inert;
      }
      previousInert.clear();
    }
  };

  // The masthead's height goes to CSS for the open panel and the sticky offsets. The observer hands it over after
  // layout, as part of its entry, so nothing is read from the page. Reading offsetHeight right after the class and
  // attribute writes below forced a synchronous layout on every page load (PageSpeed, 2026-10-07).
  let mastheadHeight = 0;
  if (masthead !== null) {
    new ResizeObserver(([entry]) => {
      const height = Math.round(entry?.borderBoxSize[0]?.blockSize ?? 0);
      if (height > 0 && height !== mastheadHeight) {
        mastheadHeight = height;
        document.documentElement.style.setProperty("--masthead-h", `${height}px`);
      }
    }).observe(masthead);
  }

  const isOpen = (): boolean => toggle.getAttribute("aria-expanded") === "true";

  const setOpen = (open: boolean): void => {
    setBackgroundInert(open);
    toggle.setAttribute("aria-expanded", String(open));
    if (label !== null) {
      label.textContent = open ? "Stäng" : "Meny";
    }
    if (open) {
      nav.removeAttribute("hidden");
      document.body.style.overflow = "hidden";
    } else {
      nav.setAttribute("hidden", "");
      document.body.style.overflow = "";
    }
  };

  const applyBreakpoint = (): void => {
    if (mobile.matches) {
      if (!isOpen()) {
        nav.setAttribute("hidden", "");
      }
    } else {
      setBackgroundInert(false);
      nav.removeAttribute("hidden");
      toggle.setAttribute("aria-expanded", "false");
      if (label !== null) {
        label.textContent = "Meny";
      }
      document.body.style.overflow = "";
    }
  };

  masthead?.classList.add("nav-ready");
  applyBreakpoint();
  mobile.addEventListener("change", applyBreakpoint);

  toggle.addEventListener("click", () => setOpen(!isOpen()));

  document.addEventListener("keydown", (event: KeyboardEvent) => {
    if (!isOpen()) {
      return;
    }

    if (event.key === "Escape") {
      setOpen(false);
      toggle.focus();
      return;
    }

    // Fokusfälla: Tab får inte vandra ut ur den öppna panelen.
    if (event.key === "Tab") {
      const items: HTMLElement[] = [
        toggle,
        ...findAll<HTMLElement>("a[href], button:not([disabled])", nav),
      ];
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) {
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  document.addEventListener("click", (event: MouseEvent) => {
    if (!isOpen()) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }
    if (!nav.contains(target) && !toggle.contains(target)) {
      setOpen(false);
    }
  });

  nav.addEventListener("click", (event: MouseEvent) => {
    const target = event.target;
    if (target instanceof Element && target.closest("a") !== null && mobile.matches) {
      setOpen(false);
    }
  });
}
